import { useIntl } from "react-intl";

import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import {
  Box,
  CircularProgress,
  Divider,
  SelectChangeEvent,
} from "@mui/material";
import { useQuery } from "react-query";
import { useState } from "react";
import { useRecoilValue } from "recoil";

import { currentUserCurrencyAtom, Roles, useHasRole } from "../../AuthProvider";
import AppButton from "../../components/AppButton";
import { useDebounce } from "../../hooks/debounce";
import { CustomAutocomplete } from "../../components/Inputs/CustomAutocomplete";
import { CustomTextField } from "../../components/Inputs/CustomTextField";
import { RevbellSelect } from "../../components/Inputs/RevbellSelect";
import {
  AccommodationSearchOption,
  PageData,
  Status,
  UhCatSearchOption,
} from "../types";
import { searchUhCats } from "../services";

interface Props {
  onUhCatSelect: (option: UhCatSearchOption | null) => void;
  onAccommodationChange: (
    accommodation: AccommodationSearchOption | null
  ) => void;
  year: number;
  onYearChange: (year: number) => void;
  years: number[];
  pageData: PageData | null;
  price: string;
  onPriceChange: (price: string) => void;
  statuses: Status[];
  selectedStatusId: number | "";
  onStatusChange: (id: number | "") => void;
  priceStatusIsDirty: boolean;
  onSavePriceStatus: () => void;
  onResetPriceStatus: () => void;
  isSavingPriceStatus: boolean;
  isExporting: boolean;
  onExport: () => void;
}

export default function CapacityFiltersBar({
  onUhCatSelect,
  onAccommodationChange,
  year,
  onYearChange,
  years,
  pageData,
  price,
  onPriceChange,
  statuses,
  selectedStatusId,
  onStatusChange,
  priceStatusIsDirty,
  onSavePriceStatus,
  onResetPriceStatus,
  isSavingPriceStatus,
  isExporting,
  onExport,
}: Props) {
  const intl = useIntl();
  const { hasRole } = useHasRole();
  const currency = useRecoilValue(currentUserCurrencyAtom);
  const [searchValue, setSearchValue] = useState("");
  const [selectedAccommodation, setSelectedAccommodation] =
    useState<AccommodationSearchOption | null>(null);
  const [uhCatSearch, setUhCatSearch] = useState("");
  const [selectedUhCat, setSelectedUhCat] = useState<UhCatSearchOption | null>(
    null
  );

  const debounced = useDebounce(searchValue, 400);
  const debouncedUhCat = useDebounce(uhCatSearch, 400);

  const { data: accommodationData, isLoading: isLoadingAccommodation } =
    useQuery(
      ["hebergements-capacite", debounced],
      () =>
        fetch(
          `/api/properties/?page_size=100&search=${encodeURIComponent(
            debounced ?? ""
          )}&ordering=hebergement_nom_court`,
          { credentials: "include" }
        ).then(async (r) => {
          if (!r.ok) {
            const err = await r.json().catch(() => ({}));
            throw new Error(err?.message ?? `HTTP ${r.status}`);
          }
          const data = await r.json();
          return {
            ...data,
            results: (data.results ?? []).map((item: any) => ({
              id: item.id,
              shortName: item.hebergement_nom_court,
            })),
          };
        }),
      { keepPreviousData: true }
    );

  const accommodationOptions: AccommodationSearchOption[] =
    accommodationData?.results ?? [];

  // The "Hébergement" (UH cat) is a standalone search (by name or id). It is
  // NOT gated on first picking a camping — selecting one auto-fills the
  // camping below (legacy parity). When a camping IS selected, the search is
  // scoped to it.
  const { data: uhCatData, isLoading: isLoadingUhCats } = useQuery(
    ["uh-cats-search", debouncedUhCat, selectedAccommodation?.id],
    () => searchUhCats(debouncedUhCat ?? "", selectedAccommodation?.id),
    { keepPreviousData: true }
  );
  const uhCatOptions: UhCatSearchOption[] = uhCatData ?? [];

  return (
    <>
      {hasRole(Roles.Pricer) && (
        <AppButton
          startIcon={
            isExporting ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <CloudUploadIcon />
            )
          }
          onClick={onExport}
          disabled={isExporting}
          sx={{ mb: 2 }}
        >
          {intl.formatMessage({ id: "CAPACITE_CUSTOM_EXPORT" })}
        </AppButton>
      )}

      <Box
        sx={{
          display: "flex",
          gap: 1,
          flexWrap: "wrap",
          alignItems: "flex-end",
          mb: 1,
        }}
      >
        <CustomAutocomplete
          size="small"
          sx={{ minWidth: 220 }}
          label={intl.formatMessage({ id: "PROPERTY" })}
          options={accommodationOptions}
          loading={isLoadingAccommodation}
          value={selectedAccommodation}
          getOptionLabel={(o) => o.shortName}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          filterOptions={(x) => x}
          onChange={(_, opt) => {
            setSelectedAccommodation(opt);
            setSelectedUhCat(null);
            onAccommodationChange(opt);
          }}
          onInputChange={(_, val, reason) => {
            if (reason === "input" || reason === "clear") {
              setSearchValue(val);
            }
          }}
          renderOptions={(props, option) => (
            <li {...props} key={option.id}>
              {option.shortName}
            </li>
          )}
        />

        <CustomAutocomplete
          size="small"
          sx={{ minWidth: 260 }}
          label={intl.formatMessage({ id: "UH_CAT" })}
          options={uhCatOptions}
          loading={isLoadingUhCats}
          value={selectedUhCat}
          getOptionLabel={(o) => o.shortName}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          filterOptions={(x) => x}
          onChange={(_, opt) => {
            setSelectedUhCat(opt);
            if (opt) {
              setSelectedAccommodation({
                id: opt.hebergementId,
                shortName: opt.hebergementShortName,
              });
            }
            onUhCatSelect(opt);
          }}
          onInputChange={(_, val, reason) => {
            if (reason === "input" || reason === "clear") {
              setUhCatSearch(val);
            }
          }}
          renderOptions={(props, option) => (
            <li {...props} key={option.id}>
              {option.shortName} — {option.hebergementShortName}
            </li>
          )}
        />

        <RevbellSelect
          label={intl.formatMessage({ id: "YEAR" })}
          size="small"
          value={year}
          onChange={(e: SelectChangeEvent<unknown>) =>
            onYearChange(Number(e.target.value))
          }
          options={years.map((y) => ({ value: y, label: String(y) }))}
          sx={{ minWidth: 100 }}
        />

        {pageData && (
          <>
            <Box sx={{ width: 160 }}>
              <CustomTextField
                label={intl.formatMessage({
                  id: "CAPACITE_CUSTOM_PURCHASE_PRICE",
                })}
                value={price}
                onChange={(e) => onPriceChange(e.target.value)}
                required={false}
                endAdornment={currency.symbol}
              />
            </Box>

            <RevbellSelect
              label={intl.formatMessage({
                id: "CAPACITE_CUSTOM_PRICING_STATUS",
              })}
              size="small"
              value={selectedStatusId}
              onChange={(e: SelectChangeEvent<unknown>) =>
                onStatusChange(e.target.value as number | "")
              }
              options={statuses.map((s) => ({
                value: s.pricingStatusId,
                label: s.pricingStatusName,
              }))}
              sx={{ minWidth: 180 }}
            />

            {priceStatusIsDirty && (
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  alignSelf: "center",
                  marginTop: 2,
                }}
              >
                <AppButton
                  size="small"
                  onClick={onSavePriceStatus}
                  disabled={isSavingPriceStatus}
                  loading={isSavingPriceStatus}
                >
                  {intl.formatMessage({ id: "SUBMIT" })}
                </AppButton>
                <AppButton
                  intent="secondary"
                  size="small"
                  onClick={onResetPriceStatus}
                  disabled={isSavingPriceStatus}
                >
                  {intl.formatMessage({ id: "RESET" })}
                </AppButton>
              </Box>
            )}
          </>
        )}
      </Box>

      <Divider sx={{ mb: 1 }} />
    </>
  );
}
